/**
 * Data Access Layer for Place Equipment
 * Manages the association between places and equipment with availability models
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type {
  PlaceEquipment,
  PlaceEquipmentCreate,
  PlaceEquipmentUpdate,
  PlaceEquipmentSummary,
  ThingWithPlaceContext,
  Thing,
} from './types.js';

/**
 * List all equipment assigned to a place
 * @param placeId - UUID of the place
 * @param availabilityType - Optional filter by availability type
 */
export async function listPlaceEquipment(
  placeId: string,
  availabilityType?: 'default' | 'on_request'
): Promise<PlaceEquipment[]> {
  let query = getClient()
    .from(TABLES.place_equipment)
    .select('*')
    .eq('place_id', placeId);

  // Multi-tenant: Filter by account_id if account context is set
  const accountId = getCurrentAccountId();
  if (accountId) {
    query = query.eq('account_id', accountId);
  }

  if (availabilityType) {
    query = query.eq('availability_type', availabilityType);
  }

  const { data, error } = await query.order('sort_order').order('created_at');

  if (error) throw error;
  return (data || []) as PlaceEquipment[];
}

/**
 * Get equipment summary for a place (with thing details)
 * Returns equipment grouped by availability type
 */
export async function getPlaceEquipmentSummary(
  placeId: string
): Promise<PlaceEquipmentSummary> {
  // Get place details
  const placeQuery = getClient()
    .from(TABLES.places)
    .select('id, name')
    .eq('id', placeId)
    .single();

  // Get place equipment with thing details using a join
  let equipmentQuery = getClient()
    .from(TABLES.place_equipment)
    .select(`
      id,
      place_id,
      thing_id,
      account_id,
      availability_type,
      default_quantity,
      notes,
      sort_order,
      metadata,
      created_at,
      updated_at,
      thing:thing_id (
        id,
        slug,
        name,
        type,
        status,
        quantity,
        account_id,
        metadata,
        created_at,
        updated_at
      )
    `)
    .eq('place_id', placeId);

  // Note: We don't filter by account_id here because place_id is already unique per place
  // and each place is associated with a specific account. The place relationship handles tenant isolation.

  const [placeResult, equipmentResult] = await Promise.all([
    placeQuery,
    equipmentQuery,
  ]);

  if (placeResult.error) throw placeResult.error;
  if (equipmentResult.error) throw equipmentResult.error;

  const place = placeResult.data;
  const equipment = equipmentResult.data || [];

  console.log('[getPlaceEquipmentSummary] Returned equipment:', equipment.map((e: any) => ({
    name: e.thing?.name,
    availability_type: e.availability_type,
    columns: Object.keys(e)
  })));

  // Group equipment by availability type
  const defaultEquipment: ThingWithPlaceContext[] = [];
  const onRequestEquipment: ThingWithPlaceContext[] = [];

  equipment.forEach((pe: any) => {
    console.log(`[getPlaceEquipmentSummary] Grouping ${pe.thing?.name}: availability_type="${pe.availability_type}" (type: ${typeof pe.availability_type})`);
    
    const thing: ThingWithPlaceContext = {
      ...pe.thing,
      place_equipment: {
        availability_type: pe.availability_type,
        default_quantity: pe.default_quantity,
        notes: pe.notes,
      },
    };

    if (pe.availability_type === 'default') {
      console.log(`  -> Adding to DEFAULT`);
      defaultEquipment.push(thing);
    } else {
      console.log(`  -> Adding to ON_REQUEST`);
      onRequestEquipment.push(thing);
    }
  });

  console.log('[getPlaceEquipmentSummary] Final result:', {
    default_count: defaultEquipment.length,
    on_request_count: onRequestEquipment.length,
    default_names: defaultEquipment.map(e => e.name),
    on_request_names: onRequestEquipment.map(e => e.name)
  });

  return {
    place_id: place.id,
    place_name: place.name,
    default_equipment: defaultEquipment,
    on_request_equipment: onRequestEquipment,
  };
}

/**
 * Get all places where a thing is available
 * @param thingId - UUID of the thing
 */
export async function listPlacesForThing(
  thingId: string
): Promise<PlaceEquipment[]> {
  const { data, error } = await getClient()
    .from(TABLES.place_equipment)
    .select('*')
    .eq('thing_id', thingId)
    .order('created_at');

  if (error) throw error;
  return (data || []) as PlaceEquipment[];
}

/**
 * Add equipment to a place
 */
export async function addPlaceEquipment(
  input: PlaceEquipmentCreate
): Promise<PlaceEquipment> {
  const accountId = getCurrentAccountId();

  const createData: any = {
    ...input,
    account_id: input.account_id || accountId,
    default_quantity: input.default_quantity ?? 1,
    sort_order: input.sort_order ?? 0,
    metadata: input.metadata ?? {},
  };

  const { data, error } = await getClient()
    .from(TABLES.place_equipment)
    .insert(createData)
    .select()
    .single();

  if (error) throw error;
  return data as PlaceEquipment;
}

/**
 * Update place equipment association
 */
export async function updatePlaceEquipment(
  id: string,
  input: PlaceEquipmentUpdate
): Promise<PlaceEquipment> {
  const { data, error } = await getClient()
    .from(TABLES.place_equipment)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as PlaceEquipment;
}

/**
 * Remove equipment from a place
 */
export async function removePlaceEquipment(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.place_equipment)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Bulk add equipment to a place
 * Useful for template application or initial setup
 */
export async function bulkAddPlaceEquipment(
  placeId: string,
  equipment: Array<{
    thing_id: string;
    availability_type: 'default' | 'on_request';
    default_quantity?: number;
    notes?: string;
    account_id: string;
  }>
): Promise<PlaceEquipment[]> {
  if (equipment.length === 0) return [];

  const createData = equipment.map((eq, index) => ({
    place_id: placeId,
    thing_id: eq.thing_id,
    availability_type: eq.availability_type,
    default_quantity: eq.default_quantity ?? 1,
    notes: eq.notes ?? null,
    sort_order: index,
    account_id: eq.account_id,
    metadata: {},
  }));

  const { data, error } = await getClient()
    .from(TABLES.place_equipment)
    .insert(createData)
    .select();

  if (error) throw error;
  return (data || []) as PlaceEquipment[];
}

/**
 * Get available equipment for a happening based on its place
 * Returns org-wide equipment + place-specific equipment
 */
export async function getAvailableEquipmentForPlace(
  placeId: string,
  accountId: string
): Promise<{
  allThings: Thing[];
  placeDefaultEquipment: ThingWithPlaceContext[];
  placeOnRequestEquipment: ThingWithPlaceContext[];
}> {
  // Get all things for the account
  const thingsQuery = getClient()
    .from(TABLES.things)
    .select('*')
    .eq('status', 'available')
    .eq('account_id', accountId);

  // Get place equipment summary
  const [thingsResult, placeSummary] = await Promise.all([
    thingsQuery,
    getPlaceEquipmentSummary(placeId),
  ]);

  if (thingsResult.error) throw thingsResult.error;

  return {
    allThings: (thingsResult.data || []) as Thing[],
    placeDefaultEquipment: placeSummary.default_equipment,
    placeOnRequestEquipment: placeSummary.on_request_equipment,
  };
}
